<template>
    <main>
      <div id="ntabiPayList">
        <div class="container">
  
          <!-- 헤더 -->
          <div class="page-head">
            <div>
              <h3>NTABI PAY 결제내역</h3>
              <p>현장정산 결제 조회 · 취소</p>
            </div>
            <button class="btn btn-new" @click="goNewPay"><i class="bi bi-plus-lg"></i> 새결제</button>
          </div>
  
          <!-- 조회조건 -->
          <div class="search-box">
            <div class="date-range">
              <input type="date" v-model="fromDate" class="form-control">
              <span>~</span>
              <input type="date" v-model="toDate" class="form-control">
            </div>
            <div class="quick">
              <button class="btn btn-q" @click="setRange(0)">오늘</button>
              <button class="btn btn-q" @click="setRange(6)">7일</button>
              <button class="btn btn-q" @click="setRange(29)">30일</button>
            </div>
            <button class="btn btn-search" :disabled="loading" @click="getList">
              {{ loading ? '조회중...' : '조회' }}
            </button>
            <input v-model.trim="keyword" type="text" class="form-control kw" placeholder="결제번호 · 결제자 · 연락처 검색">
          </div>
  
          <!-- 요약 -->
          <div class="summary">
            <div class="sum-card paid">
              <p>결제완료</p>
              <strong>{{ fmt(summary.pyAmt) }}<small>원</small></strong>
              <span>{{ summary.pyCnt }}건</span>
            </div>
            <div class="sum-card cxl">
              <p>취소</p>
              <strong>{{ fmt(summary.cxAmt) }}<small>원</small></strong>
              <span>{{ summary.cxCnt }}건</span>
            </div>
            <div class="sum-card fail">
              <p>실패·포기</p>
              <strong>{{ summary.flCnt }}<small>건</small></strong>
              <span>결제창 닫기 포함</span>
            </div>
          </div>
  
          <!-- 상태 탭 -->
          <div class="stat-tabs">
            <button
              v-for="t in statTabs"
              :key="t.code"
              class="tab"
              :class="{ active: statFilter === t.code }"
              @click="statFilter = t.code"
            >
              {{ t.label }} <em>{{ countByStat(t.code) }}</em>
            </button>
          </div>
  
          <!-- 목록 -->
          <p v-if="!loading && !filteredRows.length" class="empty">조회된 결제내역이 없습니다.</p>

          <!-- PC 컬럼 헤더 -->
          <div v-if="filteredRows.length" class="list-head">
            <span>상태</span>
            <span>일시</span>
            <span>결제번호</span>
            <span>결제타이틀</span>
            <span>결제자</span>
            <span>수단</span>
            <span>상품/수량</span>
            <span class="r">금액</span>
            <span></span>
          </div>

          <div
            v-for="r in filteredRows"
            :key="r.payNo"
            class="pay-card"
            :class="['st-' + r.payStat, { open: opened === r.payNo }]"
          >
            <div class="pc-head" @click="toggle(r.payNo)">
              <!-- PC: 컬럼형 -->
              <span class="badge-stat">{{ statLabel(r.payStat) }}</span>
              <span class="c-date">{{ fmtDate(r.payDate || r.insDate) }}</span>
              <span class="c-no">{{ r.payNo }}</span>
              <span class="c-title" :title="r.payTitle">{{ r.payTitle }}</span>
              <span class="c-payer">
                {{ r.payerName || '-' }}
                <em v-if="r.payCall">{{ fmtTel(r.payCall) }}</em>
              </span>
              <span class="c-method">{{ r.payMethod || '-' }}</span>
              <span class="c-lines" :title="linesText(r)">{{ linesText(r) }}</span>
              <span class="pc-amt">{{ fmt(r.payAmt) }}<small>원</small></span>
              <span class="c-act">
                <button
                  v-if="r.payStat === 'PY'"
                  class="btn btn-cxl-sm"
                  :disabled="cancelingNo === r.payNo"
                  @click.stop="cancelPay(r)"
                >
                  {{ cancelingNo === r.payNo ? '처리중' : '취소' }}
                </button>
                <i v-else class="bi bi-chevron-down pc-arrow"></i>
              </span>

              <!-- 모바일: 2줄형 -->
              <div class="pc-mobile">
                <div class="pc-top">
                  <span class="badge-stat">{{ statLabel(r.payStat) }}</span>
                  <span class="pc-title">{{ r.payTitle }}</span>
                </div>
                <div class="pc-sub">
                  {{ fmtDate(r.payDate || r.insDate) }} · {{ r.payNo }}
                  <template v-if="r.payerName"> · {{ r.payerName }}</template>
                </div>
              </div>
            </div>
  
            <div v-if="opened === r.payNo" class="pc-body">
              <table class="detail">
                <tbody>
                  <tr v-if="r.lines && r.lines.length">
                    <th>상품/수량</th>
                    <td>
                      <div v-for="(ln, i) in r.lines" :key="i">
                        {{ ln.salePdt }} × {{ ln.qty }} = {{ fmt(ln.lineAmt) }}원
                      </div>
                    </td>
                  </tr>
                  <tr v-if="r.payMethod"><th>결제수단</th><td>{{ r.payMethod }}</td></tr>
                  <tr v-if="r.payerName || r.payCall">
                    <th>결제자</th>
                    <td>{{ r.payerName || '-' }} <span v-if="r.payCall" class="dim">{{ fmtTel(r.payCall) }}</span></td>
                  </tr>
                  <tr><th>요청</th><td>{{ fmtDate(r.insDate) }} <span class="dim">{{ r.insId || '' }}</span></td></tr>
                  <tr v-if="r.payDate"><th>승인</th><td>{{ fmtDate(r.payDate) }}</td></tr>
                  <tr v-if="r.payStat === 'FL'">
                    <th>실패사유</th>
                    <td>{{ r.failReason || '-' }} <span class="dim">{{ r.failCode || '' }}</span></td>
                  </tr>
                  <tr v-if="r.payStat === 'CX'">
                    <th>취소</th>
                    <td>{{ fmtDate(r.cxlDate) }} <span class="dim">{{ r.cxlId || '' }}</span><br>{{ r.cxlReason || '' }}</td>
                  </tr>
                </tbody>
              </table>
  
              <button
                v-if="r.payStat === 'PY'"
                class="btn btn-cxl"
                :disabled="cancelingNo === r.payNo"
                @click.stop="cancelPay(r)"
              >
                {{ cancelingNo === r.payNo ? '취소 처리중...' : '전액 결제취소' }}
              </button>
            </div>
          </div>
  
          <p v-if="rows.length >= 1000" class="limit-note">
            최근 1,000건까지만 표시됩니다. 조회기간을 줄여주세요.
          </p>
        </div>
      </div>
    </main>
  </template>
  
  <script>
  const STAT_LABELS = { PY: "결제완료", CX: "취소", FL: "실패·포기", RP: "진행중" };
  
  function ymd(d) {
    const p = n => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }
  
  export default {
    data() {
      const today = ymd(new Date());
      return {
        fromDate: today,
        toDate: today,
        keyword: "",
  
        rows: [],
        loading: false,
        statFilter: "PY",
        opened: null,
        cancelingNo: null,
  
        statTabs: [
          { code: "PY",  label: "결제완료" },
          { code: "CX",  label: "취소" },
          { code: "FL",  label: "실패·포기" },
          { code: "RP",  label: "진행중" },
          { code: "ALL", label: "전체" },
        ],
      };
    },
  
    computed: {
      user() {
        return this.$store.state.user;
      },
  
      // 검색어만 적용한 목록 (탭 건수 계산용)
      keywordRows() {
        const kw = this.keyword.replace(/-/g, "").toLowerCase();
        if (!kw) return this.rows;
        return this.rows.filter(r =>
          [r.payNo, r.payerName, r.payCall]
            .some(v => String(v || "").replace(/-/g, "").toLowerCase().includes(kw))
        );
      },
  
      filteredRows() {
        if (this.statFilter === "ALL") return this.keywordRows;
        return this.keywordRows.filter(r => r.payStat === this.statFilter);
      },
  
      // 요약은 검색어와 무관하게 조회기간 전체 기준
      summary() {
        const s = { pyCnt: 0, pyAmt: 0, cxCnt: 0, cxAmt: 0, flCnt: 0 };
        this.rows.forEach(r => {
          const amt = Number(r.payAmt) || 0;
          if (r.payStat === "PY") { s.pyCnt++; s.pyAmt += amt; }
          else if (r.payStat === "CX") { s.cxCnt++; s.cxAmt += amt; }
          else if (r.payStat === "FL") { s.flCnt++; }
        });
        return s;
      },
    },
  
    created() {
      if (!this.checkAuth()) return;
      this.getList();
    },

    mounted() {
        if (this.user.handlerGrade >= 20) {
        this.$swal.fire({ title: '접근 불가', icon: 'error' });
        this.$router.push({ path: '/' });
        }
    document.getElementById('user').style.display = 'none';        
    // // 이 페이지에서만 상단 메뉴 숨김
    // const userEl = document.getElementById("user");
    // if (userEl) userEl.style.display = "none";
    // document.querySelectorAll(".nav-item").forEach(el => (el.style.display = "none"));
    },

    methods: {
      /* ---------- 권한 ---------- */
      checkAuth() {
        const grade = this.user?.handlerGrade;
        if (grade === undefined || grade === null || grade >= 5) {
          this.$swal.fire({ title: "로그인 필요", text: "접근권한 확인불가.", icon: "error" });
          this.$router.push({ path: "/b2bLogin" });
          return false;
        }
        return true;
      },
  
      /* ---------- 조회 ---------- */
      async getList() {
        if (!this.fromDate || !this.toDate || this.fromDate > this.toDate) {
          this.$swal.fire("", "조회기간을 확인해주세요.", "warning");
          return;
        }
  
        this.loading = true;
        const res = await this.$api("/api/ntabiTossPay/list", {
          param: [{ fromDate: this.fromDate, toDate: this.toDate }],
        });
        this.loading = false;
  
        if (res && res.ok === true) {
          this.rows = Array.isArray(res.rows) ? res.rows : [];
          this.opened = null;
        } else {
          this.rows = [];
          this.$swal.fire("", res?.message || "결제내역을 불러오지 못했습니다.", "error");
        }
      },
  
      setRange(days) {
        const to = new Date();
        const from = new Date();
        from.setDate(to.getDate() - days);
        this.fromDate = ymd(from);
        this.toDate = ymd(to);
        this.getList();
      },
  
      toggle(payNo) {
        this.opened = this.opened === payNo ? null : payNo;
      },

      // 목록 한 줄용 상품요약: "5,000 쿠폰×1, 1,000 쿠폰×2"
      linesText(r) {
        if (!r.lines || !r.lines.length) return "-";
        return r.lines.map(ln => `${ln.salePdt}×${ln.qty}`).join(", ");
      },
  
      countByStat(code) {
        if (code === "ALL") return this.keywordRows.length;
        return this.keywordRows.filter(r => r.payStat === code).length;
      },
  
      /* ---------- 결제취소 (전액) ---------- */
      async cancelPay(r) {
        if (this.cancelingNo) return;
  
        const result = await this.$swal.fire({
          icon: "warning",
          title: "결제취소",
          html: `<b>${this.fmt(r.payAmt)}원</b> 결제를 전액 취소합니다.<br><small>${r.payNo}</small><br>취소 후에는 되돌릴 수 없습니다.`,
          input: "text",
          inputPlaceholder: "취소사유 (필수)",
          inputAttributes: { maxlength: 200 },
          inputValidator: v => (!v || !v.trim() ? "취소사유를 입력해주세요." : undefined),
          showCancelButton: true,
          confirmButtonText: "결제취소",
          cancelButtonText: "닫기",
          confirmButtonColor: "#e74c3c",
        });
        if (!result.isConfirmed) return;
  
        this.cancelingNo = r.payNo;
        const res = await this.$api("/api/ntabiTossPay/cancel", {
          param: [{ payNo: r.payNo, cancelReason: result.value.trim() }],
        });
        this.cancelingNo = null;
  
        if (res && res.ok === true) {
          await this.$swal.fire("", "결제가 취소되었습니다.", "success");
        } else {
          await this.$swal.fire("", res?.message || "결제취소에 실패했습니다.", "error");
        }
        // 성공·실패 모두 서버 상태로 다시 맞춤
        await this.getList();
        this.opened = r.payNo;
      },
  
      /* ---------- 표시 ---------- */
      statLabel(code) {
        return STAT_LABELS[code] || code;
      },
  
      fmt(v) {
        return Number(v || 0).toLocaleString("ko-KR");
      },
  
      fmtDate(v) {
        if (!v) return "-";
        const d = new Date(v);
        if (isNaN(d.getTime())) return String(v);
        const p = n => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
      },
  
      fmtTel(v) {
        const s = String(v || "").replace(/\D/g, "");
        if (s.length === 11) return `${s.slice(0, 3)}-${s.slice(3, 7)}-${s.slice(7)}`;
        if (s.length === 10) return `${s.slice(0, 3)}-${s.slice(3, 6)}-${s.slice(6)}`;
        return v;
      },
  
      goNewPay() {
        this.$router.push({ path: "/ma_TossPay" });
      },
    },
  };
  </script>
  
  <style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;500;600;700&display=swap');
  
  #ntabiPayList {
    --ci-green:       #86cd53;
    --ci-green-dark:  #6ab53e;
    --ci-green-light: #f0faeb;
    --charcoal:       #1c1c1c;
    --text-main:      #1c1c1c;
    --text-sub:       #5a5a5a;
    --gray-50:        #fafaf8;
    --gray-100:       #f5f5f5;
    --gray-200:       #e8e8e8;
    --gray-400:       #aaaaaa;
    --red:            #e74c3c;
    --amber:          #e67e22;
    --radius-sm:      8px;
    --radius-md:      14px;
    --shadow-sm:      0 1px 6px rgba(0,0,0,0.07);
  
    background: var(--gray-50);
    font-family: 'Noto Sans KR', sans-serif;
    color: var(--text-main);
    padding: 3rem 0 4rem;
    min-height: 80vh;
  }
  /* PC 관리자 화면: 넓게 사용 */
  #ntabiPayList .container { max-width: 1480px; width: 96%; }
  
  /* 헤더 */
  .page-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--charcoal);
    border-radius: var(--radius-md);
    padding: 1.4rem 1.5rem;
    margin-bottom: 1rem;
    border-bottom: 3px solid var(--ci-green);
  }
  .page-head h3 {
    color: #fff;
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0 0 0.2rem;
  }
  .page-head p {
    color: var(--ci-green);
    font-size: 0.85rem;
    margin: 0;
  }
  .btn-new {
    background: var(--ci-green);
    color: var(--charcoal);
    font-weight: 700;
    border: none;
    border-radius: var(--radius-sm);
    padding: 9px 16px;
    white-space: nowrap;
  }
  .btn-new:hover { background: var(--ci-green-dark); color: var(--charcoal); }
  
  /* 조회조건 */
  .search-box {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    background: #fff;
    border: 1px solid var(--gray-200);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-sm);
    padding: 0.9rem 1rem;
    margin-bottom: 1rem;
  }
  .date-range {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .date-range .form-control { width: 150px; }
  .quick { display: flex; gap: 4px; }
  .btn-q {
    background: var(--gray-100);
    border: 1px solid var(--gray-200);
    color: var(--text-sub);
    font-size: 0.82rem;
    padding: 6px 10px;
    border-radius: 6px;
  }
  .btn-q:hover { background: var(--gray-200); }
  .btn-search {
    background: var(--charcoal);
    color: #fff;
    font-weight: 700;
    border-radius: var(--radius-sm);
    padding: 7px 18px;
  }
  .btn-search:hover:not(:disabled) { background: #333; color: #fff; }
  .kw { flex: 1 1 220px; }
  #ntabiPayList .form-control:focus {
    border-color: var(--ci-green);
    box-shadow: 0 0 0 3px rgba(134,205,83,0.15);
  }
  
  /* 요약 */
  .summary {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-bottom: 1rem;
  }
  .sum-card {
    background: #fff;
    border: 1px solid var(--gray-200);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-sm);
    padding: 0.9rem 1rem;
    border-left: 4px solid var(--gray-200);
  }
  .sum-card p {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text-sub);
    margin: 0 0 0.3rem;
  }
  .sum-card strong {
    display: block;
    font-size: 1.35rem;
    font-weight: 800;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
  }
  .sum-card strong small {
    font-size: 0.8rem;
    font-weight: 500;
    margin-left: 2px;
    color: var(--text-sub);
  }
  .sum-card span {
    font-size: 0.78rem;
    color: var(--gray-400);
  }
  .sum-card.paid { border-left-color: var(--ci-green); }
  .sum-card.paid strong { color: var(--ci-green-dark); }
  .sum-card.cxl { border-left-color: var(--gray-400); }
  .sum-card.fail { border-left-color: var(--amber); }
  
  /* 상태 탭 */
  .stat-tabs {
    display: flex;
    gap: 6px;
    overflow-x: auto;
    margin-bottom: 0.8rem;
  }
  .tab {
    flex: none;
    background: #fff;
    border: 1px solid var(--gray-200);
    border-radius: 999px;
    padding: 6px 14px;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-sub);
  }
  .tab em {
    font-style: normal;
    margin-left: 4px;
    color: var(--gray-400);
  }
  .tab.active {
    background: var(--charcoal);
    border-color: var(--charcoal);
    color: #fff;
  }
  .tab.active em { color: var(--ci-green); }
  
  /* 목록 */
  .empty, .limit-note {
    text-align: center;
    color: var(--gray-400);
    font-size: 0.9rem;
    padding: 2.5rem 0;
  }
  .limit-note { padding: 1rem 0; }
  
  .pay-card {
    background: #fff;
    border: 1px solid var(--gray-200);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-sm);
    margin-bottom: 8px;
    overflow: hidden;
    border-left: 4px solid var(--gray-200);
  }
  .pay-card.st-PY { border-left-color: var(--ci-green); }
  .pay-card.st-CX { border-left-color: var(--gray-400); }
  .pay-card.st-FL { border-left-color: var(--amber); }
  .pay-card.st-RP { border-left-color: #3498db; }
  
  /* 컬럼 공통 그리드 (헤더와 행이 같은 폭을 쓰도록) */
  .list-head,
  .pc-head {
    display: grid;
    grid-template-columns: 84px 128px 190px minmax(150px, 1fr) 170px 76px minmax(180px, 1.5fr) 120px 64px;
    align-items: center;
    column-gap: 14px;
  }
  .list-head {
    padding: 0.5rem 1rem 0.5rem calc(1rem + 4px);
    font-size: 0.78rem;
    font-weight: 700;
    color: var(--text-sub);
  }
  .list-head .r { text-align: right; }
  .pc-head {
    padding: 0.8rem 1rem;
    cursor: pointer;
    font-size: 0.88rem;
  }
  .pc-head:hover { background: var(--gray-50); }
  .pc-head > span { min-width: 0; }
  .c-date, .c-no, .c-method { color: var(--text-sub); font-size: 0.84rem; }
  .c-no { font-variant-numeric: tabular-nums; letter-spacing: -0.2px; }
  .c-title, .c-lines {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .c-title { font-weight: 700; }
  .c-lines { color: var(--text-sub); font-size: 0.84rem; }
  .c-payer em {
    display: block;
    font-style: normal;
    font-size: 0.76rem;
    color: var(--gray-400);
  }
  .c-act { text-align: right; }
  .pc-head .badge-stat { justify-self: start; }
  .pc-mobile { display: none; }
  .btn-cxl-sm {
    background: #fff;
    color: var(--red);
    border: 1.5px solid var(--red);
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 700;
    padding: 3px 12px;
  }
  .btn-cxl-sm:hover:not(:disabled) { background: #fdf0ee; color: var(--red); }
  .pc-main { flex: 1; min-width: 0; }
  .pc-top {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 3px;
  }
  .badge-stat {
    flex: none;
    font-size: 0.72rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 999px;
    background: var(--gray-100);
    color: var(--text-sub);
  }
  .st-PY .badge-stat { background: var(--ci-green-light); color: var(--ci-green-dark); }
  .st-CX .badge-stat { background: var(--gray-100); color: var(--gray-400); }
  .st-FL .badge-stat { background: #fef3e6; color: var(--amber); }
  .st-RP .badge-stat { background: #eaf4fc; color: #2c80b9; }
  
  .pc-title {
    font-weight: 700;
    font-size: 0.95rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .pc-sub {
    font-size: 0.78rem;
    color: var(--gray-400);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .pc-amt {
    text-align: right;
    font-weight: 800;
    font-size: 1.05rem;
    font-variant-numeric: tabular-nums;
  }
  .pc-amt small {
    font-size: 0.75rem;
    font-weight: 500;
    margin-left: 1px;
    color: var(--text-sub);
  }
  .st-CX .pc-amt { color: var(--gray-400); text-decoration: line-through; }
  .st-FL .pc-amt, .st-RP .pc-amt { color: var(--gray-400); }
  .pc-arrow {
    flex: none;
    color: var(--gray-400);
    transition: transform 0.2s;
  }
  .pay-card.open .pc-arrow { transform: rotate(180deg); }
  
  .pc-body {
    border-top: 1px dashed var(--gray-200);
    padding: 0.8rem 1rem 1rem;
    background: #fcfcfb;
  }
  .detail {
    width: 100%;
    font-size: 0.85rem;
  }
  .detail th,
  .detail td {
    padding: 0.35rem 0.2rem;
    vertical-align: top;
  }
  .pc-body { padding-left: calc(1rem + 84px + 14px); }
  .detail th {
    width: 80px;
    color: var(--text-sub);
    font-weight: 600;
  }
  .dim {
    color: var(--gray-400);
    font-size: 0.8rem;
    margin-left: 4px;
  }
  .btn-cxl {
    margin-top: 0.8rem;
    width: 220px;
    background: #fff;
    color: var(--red);
    border: 1.5px solid var(--red);
    border-radius: var(--radius-sm);
    font-weight: 700;
    padding: 10px;
  }
  .btn-cxl:hover:not(:disabled) { background: #fdf0ee; color: var(--red); }
  
  #ntabiPayList .btn:disabled { opacity: 0.5; cursor: not-allowed; }
  
  /* 태블릿·모바일: 컬럼형 대신 2줄형 */
  @media (max-width: 1100px) {
    .list-head { display: none; }
    .pc-head {
      display: flex;
      gap: 12px;
    }
    .pc-head > span:not(.pc-amt):not(.c-act) { display: none; }
    .pc-mobile { display: block; flex: 1; min-width: 0; order: -1; }
    .pc-mobile .badge-stat { display: inline-block; }
    .pc-body { padding-left: 1rem; }
    .btn-cxl { width: 100%; }
  }

  /* 모바일 */
  @media (max-width: 575px) {
    #ntabiPayList { padding: 1.5rem 0 3rem; }
    .page-head { padding: 1.1rem 1rem; }
    .page-head h3 { font-size: 1.1rem; }
    .date-range { width: 100%; }
    .date-range .form-control { flex: 1; width: auto; }
    .btn-search { flex: 1; }
    .kw { flex-basis: 100%; }
    .summary { gap: 6px; }
    .sum-card { padding: 0.7rem 0.75rem; }
    .sum-card strong { font-size: 1.05rem; }
    .pc-head { padding: 0.75rem 0.8rem; gap: 8px; }
    .pc-amt { font-size: 0.95rem; }
  }
  </style>